import { useQuery } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { REVIEW_STAGES, type ReviewStage } from '@maxus/common'
import {
  Button,
  PanelVideoSummary,
  Separator,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  useNotification,
} from '@maxus/website/components'
import { trpc } from '@maxus/website/lib'

import { modificationManager } from './modification-manager'
import { ModificationMasterRow } from './modification-master-row'
import { ModificationDrawerParams, PlatformOption } from './type'
import { useMaster } from './use-master'

// ----------------- 类型 -----------------

export type ModificationPanelProps = ModificationDrawerParams & { onClose: () => void }

export const ModificationPanel = ({ onClose, ...props }: ModificationPanelProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [tab, setTab] = useState<ReviewStage>('Demo')
  const {
    masters,
    addMaster,
    removeMaster,
    clearMasterPlatforms,
    toggleMasterPlatform,
  } = useMaster()
  const [renderMaster, setRenderMaster] = useState(true)
  const [syncCommentsEnabled, setSyncCommentsEnabled] = useState(true)
  const [note, setNote] = useState('')
  const { data: platforms } = useQuery(trpc.project.platform.list.queryOptions())
  const platformOptions: PlatformOption[] = (platforms?.items ?? []).filter(p => p.code !== 'default')
  const canRenderMaster = tab === 'Distribution' && props.stageName !== 'Distribution'

  const handleCreate = () => {
    if (!props.davinciProjectName?.trim()) {
      notify({
        status: 'error',
        title: t('Required'),
        description: t('DaVinci project name is not configured for this project.'),
      })
      return
    }
    if (modificationManager.isRunning(props.timelineVersionId)) {
      notify({ title: t('Processing') })
      return
    }

    // Distribution 阶段要求每条母带至少选择一个平台
    if (tab === 'Distribution') {
      const hasPlatform = masters.some(m => m.platforms.length > 0)
      if (!hasPlatform) {
        notify({
          status: 'error',
          title: t('Required'),
          description: t('Please select at least one platform for a master.'),
        })
        return
      }
    }

    const started = modificationManager.start({
      sourceTimelineVersionId: props.timelineVersionId,
      sourceTimelineVersionAssetId: props.timelineVersionAssetId,
      sourceStageName: props.stageName,
      stageName: tab,
      renderMaster: canRenderMaster && renderMaster,
      syncCommentsEnabled,
      note: note.trim() || undefined,
      masters:
        tab === 'Distribution'
          ? masters
              .filter(master => master.platforms.length > 0)
              .map(master => ({
                platformIds: [...master.platforms],
                platformNames: master.platforms.flatMap(platformId => {
                  const platform = platformOptions.find(({ id }) => id === platformId)
                  return platform ? [platform.name] : []
                }),
              }))
          : [],
    })
    if (started) {
      onClose()
    } else {
      notify({ title: t('Processing') })
    }
  }

  return (
    <div className="box-border px-6 py-3 w-full h-full flex flex-col gap-3 rounded-l-panel border-l border-border-subtle bg-surface-panel">
      {/* header */}
      <div className="shrink-0 grow-0 w-full flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-base font-semibold leading-6 text-text-primary">{t('Create Modification')}</span>
          <span className="text-xs leading-[18px] text-text-secondary">
            {syncCommentsEnabled
              ? t('Automatically carries review comment markers and creates a new timeline.')
              : t('Creates a new timeline.')}
          </span>
        </div>

        <Button aria-label={t('Close panel')} onClick={onClose} size="icon-sm" type="button" variant="ghost">
          <X className="size-5" />
        </Button>
      </div>

      <Separator />

      {/* version info */}
      <PanelVideoSummary {...props} />

      <Separator />

      {/* stage */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-text-primary">{t('Stage for New Modification')} *</h2>
        <Tabs className="shrink-0 px-3 py-[9px]" onValueChange={value => setTab(value as ReviewStage)} value={tab}>
          <TabsList className="grid !h-10 w-full grid-cols-4 bg-surface-card">
            {REVIEW_STAGES.map(stage => (
              <TabsTrigger
                className="data-active:bg-surface-active data-active:text-text-primary dark:data-active:bg-surface-active"
                value={stage}
              >
                {stage}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center justify-between">
          <label className="text-base font-semibold text-text-primary" htmlFor="modification-sync-comments">
            {t('Sync comments')}
          </label>
          <Switch
            checked={syncCommentsEnabled}
            id="modification-sync-comments"
            onCheckedChange={setSyncCommentsEnabled}
          />
        </div>
        {canRenderMaster && (
          <div className="flex items-center justify-between">
            <label className="text-base font-semibold text-text-primary" htmlFor="modification-render-master">
              {t('Render master')}
            </label>
            <Switch checked={renderMaster} id="modification-render-master" onCheckedChange={setRenderMaster} />
          </div>
        )}
        <div className="grid gap-2">
          <label className="text-base font-semibold text-text-primary" htmlFor="modification-note">
            {t('Note')}
          </label>
          <textarea
            aria-label={t('Note')}
            className="min-h-20 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-tertiary focus-visible:border-brand-primary focus-visible:ring-1 focus-visible:ring-focus-ring"
            id="modification-note"
            maxLength={100}
            onChange={event => setNote(event.target.value)}
            placeholder={t('Add a note (optional)')}
            value={note}
          />
        </div>
      </div>

      {tab === 'Distribution' && (
        <>
          <Separator />
          <section className="mt-5 space-y-2">
            <h2 className="text-base font-semibold text-text-primary">{t('Confirm Master Count & Platforms')} *</h2>
            <p className="text-xs leading-5 text-text-tertiary">
              {t('If one master cannot cover all platforms, click below to add more masters.')}
            </p>
          </section>
          <div className="mt-4 space-y-3">
            {masters.map((master, index) => (
              <ModificationMasterRow
                disabledPlatformIds={
                  new Set(
                    masters
                      .filter(other => other.id !== master.id)
                      .flatMap(other => other.platforms),
                  )
                }
                index={index}
                key={master.id}
                onClearPlatforms={clearMasterPlatforms}
                master={master}
                onRemove={removeMaster}
                onTogglePlatform={toggleMasterPlatform}
                platformOptions={platformOptions}
                removable={masters.length > 1}
              />
            ))}
          </div>
          <Button
            className="mt-4 h-11 w-full bg-surface-active hover:bg-surface-elevated"
            onClick={addMaster}
            variant="secondary"
          >
            <Plus className="size-4" />
            {t('Add Master')}
          </Button>
        </>
      )}

      <div className="grow shrink w-full h-1"></div>

      {/* footer */}
      <>
        <Separator />
        <div className="flex w-full shrink-0 grow-0 justify-end">
          <Button className="h-auto rounded-md px-7.5 py-2.5 text-sm font-semibold" onClick={handleCreate}>
            {t('Create')}
          </Button>
        </div>
      </>
    </div>
  )
}
